import { PropsWithChildren, useEffect } from 'react';

import { useLocation, useParams } from 'react-router';

import { addTag } from '@core';
import { EVENT_TYPES, useEventLogs, useNotificationSubscription } from '@hooks';
import {
    useCategories,
    useGuides,
    useI18n,
    useI18nMarkdown,
    useRelations,
    useUiGraph,
    useUiGraphLine,
    useUiNodes,
    useUiParameters,
} from '@queries';
import {
    categoriesAtom,
    graphLinesAtom,
    graphsAtom,
    parametersAtom,
    relationsAtom,
    remoteI18nMapsAtom,
    remoteI18nMarkdownMapsAtom,
    swingGuidesAtom,
    uiNodesAtom,
    urlParamsAtom,
    useSyncAtom,
} from '@store';

/**
 * We use this component to declare global queries and load them in the cache.
 * We consume these queries by attaching them to query atoms.
 *
 * For example:
 * ```
 * export const currentActivityQueryAtom = atomWithQuery<Activity>(
 *     () => ({
 *         queryKey: getCurrentActivityKey(),
 *     }),
 *     () => queryClient,
 * );
 * ```
 *
 * will be attached to
 *
 * `useReadOnlyCurrentActivity()`.
 */
export const GlobalLoader = ({ children }: PropsWithChildren) => {
    // Add globally loaded (or preloaded) queries here:
    const nodesRequest = useUiNodes();
    useSyncAtom(uiNodesAtom, nodesRequest.data);
    const categoriesRequest = useCategories();
    useSyncAtom(categoriesAtom, categoriesRequest.data);
    const relationsRequest = useRelations();
    useSyncAtom(relationsAtom, relationsRequest.data);

    const parametersRequest = useUiParameters();
    useSyncAtom(parametersAtom, parametersRequest.data);
    const graphsRequest = useUiGraph();
    useSyncAtom(graphsAtom, graphsRequest.data);
    const graphLinesRequest = useUiGraphLine();
    useSyncAtom(graphLinesAtom, graphLinesRequest.data);
    const guidesRequest = useGuides();
    useSyncAtom(swingGuidesAtom, guidesRequest.data);

    // Add global useEffects here:
    useNotificationSubscription();

    const i18nRequest = useI18n();
    useSyncAtom(remoteI18nMapsAtom, i18nRequest.data);
    const i18nMarkdownRequest = useI18nMarkdown();
    useSyncAtom(remoteI18nMarkdownMapsAtom, i18nMarkdownRequest.data);
    return children;
};

const DEVICE_TYPE = import.meta.env['VITE_PUBLIC_DEVICE_TYPE'];

/**
 * Must be a child of Router component.
 * Loads params to atoms for global usage.
 */
export const ParamsLoader = ({ children }: PropsWithChildren) => {
    const params = useParams();
    const location = useLocation();

    Object.entries(params ?? {}).forEach(([key, val]) => addTag(key, val ?? 'None'));

    const logEvent = useEventLogs();
    const deviceType = String(DEVICE_TYPE).toLowerCase() as 'kiosk' | 'home';
    useEffect(() => {
        logEvent(EVENT_TYPES.PAGE_VIEW, deviceType);
    }, [logEvent, params, location]);
    /**
     * We want atoms to dynamically load data based on state changes. Here we sync window location params to jotai.
     */
    useSyncAtom(urlParamsAtom, params);
    return children;
};
